import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import { useCreateChangeRequest } from '@/hooks/client-portal';
import { ApiError } from '@/lib/api';
import { formatDay, formatWallTime } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import type { ClientJobDetail } from '@shared/schemas/client-portal';
import type { ChangeRequestKindValue } from '@shared/schemas/change-request';

/*
 * Asking us to move or cancel a booked clean — ROADMAP.md §5.6, question 12.
 *
 * **Nothing here changes the job.** It sends a request; the office confirms and
 * the confirmation is what moves it. The copy says so in the dialog, because a
 * customer who presses "Cancel this clean" and is told "sent" would otherwise
 * reasonably assume the cleaner is not coming.
 *
 * This is P19: the prototype has no way for a client to ask, which is why the
 * business gets phone calls.
 */

export function ChangeRequestDialog({
  job,
  kind,
  onClose,
}: {
  job: ClientJobDetail;
  /** Null when the dialog is closed. */
  kind: ChangeRequestKindValue | null;
  onClose: () => void;
}) {
  return (
    <Dialog.Root
      open={kind !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          {kind ? <ChangeRequestForm job={job} kind={kind} onDone={onClose} /> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ChangeRequestForm({
  job,
  kind,
  onDone,
}: {
  job: ClientJobDetail;
  kind: ChangeRequestKindValue;
  onDone: () => void;
}) {
  const toast = useToast();
  const create = useCreateChangeRequest();

  const [requestedDate, setRequestedDate] = useState('');
  const [requestedTime, setRequestedTime] = useState('');
  const [notes, setNotes] = useState('');

  const error = create.error instanceof ApiError ? create.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent) {
    event.preventDefault();

    create.mutate(
      kind === 'reschedule'
        ? {
            kind: 'reschedule',
            jobId: job.id,
            requestedDate,
            requestedTime: requestedTime === '' ? null : requestedTime,
            notes,
          }
        : { kind: 'cancellation', jobId: job.id, notes },
      {
        onSuccess: () => {
          toast.success('Sent. We will come back to you — nothing has changed yet.');
          onDone();
        },
      },
    );
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">
        {kind === 'reschedule' ? 'Ask us to change the date' : 'Ask us to cancel this clean'}
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        {job.serviceTypeName} · {formatDay(job.scheduledDate)} at{' '}
        {formatWallTime(job.scheduledTime)}
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-4">
        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        {/*
          Said before the button, not after. A customer who presses "Cancel this
          clean" and is told "sent" would reasonably assume nobody is coming.
        */}
        <p className="rounded-sm border border-honey-500 bg-honey-50 px-3 py-2.5 text-meta text-bark-900">
          <span className="font-medium">This is a request, not a change.</span> Your clean stays as
          it is until we confirm. If it is urgent, ring the office on (435) 238-3286.
        </p>

        {kind === 'reschedule' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="New date"
              name="requestedDate"
              type="date"
              required
              value={requestedDate}
              error={fieldErrors.requestedDate}
              onChange={(event) => {
                setRequestedDate(event.target.value);
              }}
            />
            <Field
              label="New time"
              name="requestedTime"
              type="time"
              value={requestedTime}
              error={fieldErrors.requestedTime}
              onChange={(event) => {
                setRequestedTime(event.target.value);
              }}
              // Leaving it blank keeps the time it already has, which is what a
              // client asking for "the Thursday instead" means.
              hint="Optional — leave it blank to keep the same time."
            />
          </div>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="change-notes">Anything you want to tell us</Label>
          <textarea
            id="change-notes"
            name="notes"
            rows={3}
            value={notes}
            onChange={(event) => {
              setNotes(event.target.value);
            }}
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
            placeholder={
              kind === 'reschedule'
                ? 'We are away that week.'
                : 'We have had to change our plans, sorry.'
            }
          />
        </div>

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <Dialog.Close asChild>
            <Button variant="outline" disabled={create.isPending}>
              Never mind
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Sending…' : 'Send request'}
          </Button>
        </div>
      </form>
    </>
  );
}
